import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { ReadSearchApiResponse, SearchResultEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';

import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';

import { ContactTypeDropdown } from './components/dropdown/contactTypeDropdown';
import { ContactPreview } from './components/preview/contactPreview';
import { ContactResult } from './components/result/contactResult';

type UserResult = SearchResultEnriched<'users'>;
type CompanyResult = SearchResultEnriched<'companies'>;

type ContactsResultsProps<TScope extends 'users' | 'companies' = 'users'> = {
  scope: TScope;
  onChangeScope: (scope: 'users' | 'companies') => void;
  meta?: ReadSearchApiResponse['meta'];
} & (TScope extends 'users'
  ? { results: readonly UserResult[] }
  : { results: readonly CompanyResult[] });

/**
 * Represents a component that is used to render the contacts tab of the search
 * results
 */
export const ContactsResults = <
  TScope extends 'users' | 'companies' = 'users',
>({
  scope,
  results,
  meta,
  onChangeScope,
}: ContactsResultsProps<TScope>): JSX.Element => {
  const contactType = scope === 'companies' ? 'company' : 'user';

  const [previewedResult, setPreviewedResult] = useState<
    UserResult | CompanyResult | null
  >(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedResult,
  });

  const handleNavigateToContact = useCallback(() => {
    ampli.searchContactPreviewGoToClicked({ type: contactType });
  }, [contactType]);

  const handleSelectResult = useCallback(
    (result: UserResult | CompanyResult) => {
      if (result.result.id === previewedResult?.result.id) return;

      setPreviewedResult(result);
      ampli.searchContactPreviewOpened({ type: contactType });
    },
    [contactType, previewedResult, setPreviewedResult],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedResult(null);
    ampli.searchContactPreviewClosed({ type: contactType });
  }, [contactType, setPreviewedResult]);

  const handleChangeContactType = useCallback(
    (type: typeof contactType) => {
      setPreviewedResult(null);
      ampli.searchContactTypeChanged({ type });

      const newScope = type === 'company' ? 'companies' : 'users';
      onChangeScope(newScope);
    },
    [onChangeScope],
  );

  const noResults = !meta?.total_results;
  if (noResults) {
    return <SearchResultsEmpty />;
  }

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom>
          <Column.Slot placement="start">
            <ContactTypeDropdown
              selected={contactType}
              onChange={handleChangeContactType}
            />
          </Column.Slot>
        </Column.Row>

        <ContactResult
          results={results}
          meta={meta}
          selectedId={previewedResult?.result.id}
          onSelectResult={handleSelectResult}
        />
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedResult ? (
            <ContactPreview
              result={previewedResult}
              onGoToContact={handleNavigateToContact}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a contact"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of the contact you&apos;re looking
                      for? <br /> Go ahead and click a contact to see it here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
